/**
 * ApplicationSelector Component
 *
 * Displays a dropdown of available applications for API key selection.
 * Shows application name and masked API key. Handles application selection
 * and displays a message when no applications are available.
 *
 * Requirements:
 * - 16.1: Display selection control for choosing from available applications
 * - 16.2: Use API key from selected application for X-API-Key header
 * - 16.3: Display message when no applications available
 * - 16.4: Display application name and masked API key in selection control
 */

import { useState, useEffect } from 'react';

export interface Application {
  id: string;
  name: string;
  api_key: string;
}

export interface ApplicationSelectorProps {
  /** Array of available applications */
  applications: Application[];
  /** Currently selected application */
  selectedApplication: Application | null;
  /** Callback when application selection changes */
  onSelect: (application: Application | null) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * Mask an API key for display, showing only first 8 and last 4 characters
 * Example: mk_live_abc123xyz789 -> mk_live_...xyz789
 */
const maskApiKey = (apiKey: string): string => {
  if (!apiKey || apiKey.length <= 12) {
    return apiKey;
  }

  const prefix = apiKey.substring(0, 8);
  const suffix = apiKey.substring(apiKey.length - 4);
  return `${prefix}...${suffix}`;
};

/**
 * ApplicationSelector component displays a dropdown of available applications.
 *
 * @param applications - Array of available applications
 * @param selectedApplication - Currently selected application
 * @param onSelect - Callback function when selection changes
 * @param disabled - Whether the selector is disabled
 */
export const ApplicationSelector = ({
  applications,
  selectedApplication,
  onSelect,
  disabled = false,
}: ApplicationSelectorProps) => {
  const [application, setApplication] = useState<Application | null>(selectedApplication);

  /**
   * Sync internal state with prop changes
   */
  useEffect(() => {
    setApplication(selectedApplication);
  }, [selectedApplication]);

  /**
   * Handle application selection change
   */
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const applicationId = event.target.value;

    if (!applicationId) {
      setApplication(null);
      onSelect(null);
      return;
    }

    const app = applications.find((a) => a.id === applicationId);
    if (app) {
      setApplication(app);
      onSelect(app);
    }
  };

  /**
   * Format application option text for display (Requirement 16.4)
   */
  const formatApplicationOption = (app: Application): string => {
    const maskedKey = maskApiKey(app.api_key);
    return `${app.name} (${maskedKey})`;
  };

  return (
    <div className="space-y-2">
      <label htmlFor="application-selector" className="block text-sm font-medium text-gray-700">
        Select Application (API Key)
        <span className="text-red-500 ml-1" aria-label="required">
          *
        </span>
      </label>

      {/* Application count indicator */}
      {applications.length > 0 && (
        <p className="text-xs text-gray-500">
          {applications.length} application{applications.length !== 1 ? 's' : ''} available
        </p>
      )}

      {/* No applications message (Requirement 16.3) */}
      {applications.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex items-start gap-2">
            <span className="text-yellow-600 text-lg" aria-hidden="true">
              ⚠️
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">No Applications Available</p>
              <p className="text-xs text-yellow-700 mt-1">
                You need to create an application to test API endpoints. Applications provide the
                API keys required for gateway authentication.
              </p>
              <a
                href="/applications"
                className="inline-block mt-2 text-xs font-medium text-yellow-800 hover:text-yellow-900 underline"
              >
                Create an application →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Application dropdown selector (Requirements 16.1, 16.4) */}
      {applications.length > 0 && (
        <select
          id="application-selector"
          value={application?.id || ''}
          onChange={handleChange}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          aria-label="Select application for API key"
        >
          <option value="">-- Select an application --</option>
          {applications.map((app) => (
            <option key={app.id} value={app.id}>
              {formatApplicationOption(app)}
            </option>
          ))}
        </select>
      )}

      {/* Selected application details (Requirement 16.4) */}
      {application && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start gap-2">
            <span className="text-blue-600 text-lg" aria-hidden="true">
              🔑
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">{application.name}</p>
              <p className="text-xs text-blue-700 mt-1 font-mono">
                API Key: {maskApiKey(application.api_key)}
              </p>
              <p className="text-xs text-blue-600 mt-2">
                This API key will be used for gateway authentication in the X-API-Key header.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-gray-500 mt-2">
        Select the application whose API key you want to use for testing. The API key authenticates
        your requests with the API Gateway.
      </p>
    </div>
  );
};

export default ApplicationSelector;
